import { StatusTypes } from '@grin-rnd/grin-api-sdk/dist/Enums/Statuses'
import {
  OralHygieneAggregationKeysByListType,
  OralHygieneCategoriesByListType,
  OralHygieneListTypes,
  OralHygieneSearchFieldExistenceFilterByListType,
  OralHygieneSearchFieldsByListType,
  OralHygieneWidgets
} from 'consts/oralHygieneReportConsts'
import moment from 'moment'

export const getOralHygieneDateLowerBound = (pastXDays, now) =>
  pastXDays ? moment(now).subtract(pastXDays, 'days').toISOString() : null

/**
 * Builds the searchPatientSearchModels filter shared by every Oral Hygiene patient view:
 * exact account owner, allowed statuses, the selected rolling period on the latest OH-scored
 * scan date, existence of the view's field, and the selected categories with OR semantics.
 * Records-only patients (leads without a Patient entity) are allowed, matching the aggregation
 * in grin-copilot-service, so the lists add up to the widget counts.
 */
export const buildOralHygienePatientsFilter = ({ accountOwnerId, listType, categories = [], pastXDays, now }) => {
  const viewSearchField = OralHygieneSearchFieldsByListType[listType]

  const filter = {
    doctorId: { eq: accountOwnerId },
    [viewSearchField]: OralHygieneSearchFieldExistenceFilterByListType[listType],
    and: [
      {
        or: [
          { statusType: { eq: StatusTypes.ActiveTreatment } },
          { statusType: { eq: StatusTypes.Observation } },
          { statusType: { eq: StatusTypes.Retention } },
          { statusType: { eq: StatusTypes.NewLead } },
          { statusType: { eq: StatusTypes.RecordsOnly } }
        ]
      }
    ]
  }

  const dateLowerBound = getOralHygieneDateLowerBound(pastXDays, now)
  if (dateLowerBound) {
    filter.lastOhScanDate = { gte: dateLowerBound }
  }

  if (categories.length) {
    const selectedCategories = OralHygieneCategoriesByListType[listType].filter(category =>
      categories.includes(category.key)
    )
    filter.and.push({
      or: selectedCategories.map(category => ({ [viewSearchField]: { eq: category.searchValue } }))
    })
  }

  return filter
}

/**
 * Maps the aggregation endpoint response to the complete category totals of the given view.
 * Chip counts always come from here, so filtering rows never recalculates them.
 */
export const getOralHygieneCategoryCounts = (aggregationData, listType) => {
  const distribution = aggregationData?.[OralHygieneAggregationKeysByListType[listType]] || {}

  return OralHygieneCategoriesByListType[listType].reduce(
    (counts, category) => ({ ...counts, [category.key]: distribution[category.key] || 0 }),
    {}
  )
}

export const getOralHygieneViewTotal = (aggregationData, listType) =>
  Object.values(getOralHygieneCategoryCounts(aggregationData, listType)).reduce((sum, count) => sum + count, 0)

/**
 * What `See patients` on a widget opens. A number card counts a single category, so its list
 * opens filtered to that category and matches the card count; the trend widget shows the whole
 * distribution, so its list opens with All selected.
 */
const SEE_PATIENTS_TARGETS_BY_WIDGET = {
  [OralHygieneWidgets.HygieneTrend]: { listType: OralHygieneListTypes.HygieneTrend, categories: [] },
  [OralHygieneWidgets.HighCavityRisk]: { listType: OralHygieneListTypes.CavityRisk, categories: ['high'] },
  [OralHygieneWidgets.HighPerioRisk]: { listType: OralHygieneListTypes.PerioRisk, categories: ['high'] },
  [OralHygieneWidgets.HygienePayAttention]: {
    listType: OralHygieneListTypes.HygieneScore,
    categories: ['payAttention']
  }
}

export const getOralHygieneSeePatientsTarget = widget => SEE_PATIENTS_TARGETS_BY_WIDGET[widget]
